import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaChartBar } from 'react-icons/fa';
import {
  ProfileCard,
  CardTitle,
  CardContent,
  ElectionChart,
  BarRow,
  BarRowLabel,
  BarRowTrack,
  BarRowFill,
  BarRowValue,
} from './styles';
import { listElections } from '../../services/platformService';

const mono = "'JetBrains Mono', 'Consolas', monospace";

const Header = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin: 0 0 0.85rem;
`;

const PeriodSelect = styled.select`
  margin-left: auto;
  background: rgba(11, 19, 32, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 5px;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.78rem;
  padding: 0.3rem 0.55rem;
  cursor: pointer;
  outline: none;
  transition: border-color 0.18s ease;

  &:hover, &:focus { border-color: rgba(0, 180, 216, 0.4); }

  option {
    background: var(--color-tertiary);
    color: var(--text-primary);
  }
`;

const Hint = styled.p`
  margin: 0.85rem 0 0;
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  color: var(--text-disabled);
  line-height: 1.5;
`;

const Empty = styled.div`
  padding: 1rem;
  text-align: center;
  color: var(--text-disabled);
  font-size: 0.85rem;
  font-family: ${mono};
`;

const spin = keyframes`to { transform: rotate(360deg); }`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 1rem;
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border: 2px solid rgba(0, 180, 216, 0.25);
    border-top-color: var(--btn-primary);
    border-radius: 50%;
    animation: ${spin} 0.9s linear infinite;
  }
`;

// Fallback estático (resultados oficiales 2023 · San Nicolás).
// Se usa cuando la tabla election_results aún no fue poblada para esa fecha.
const FALLBACK = {
  '2023': {
    label: 'San Nicolás · 2023',
    rows: [
      { label: 'Juntos por el Cambio', pct: 42.2, color: '#FFCC00' },
      { label: 'Unión por la Patria', pct: 30.0, color: '#75AADB' },
      { label: 'La Libertad Avanza', pct: 20.4, color: '#8A2BE2' },
      { label: 'Primero San Nicolás', pct: 4.5, color: '#00B4D8', highlight: true },
      { label: 'Otros', pct: 2.9, color: '#6B7280' },
    ],
    source: 'fallback',
  },
};

const PALETTE_BY_FORCE = {
  'la libertad avanza': '#8A2BE2',
  'lla': '#8A2BE2',
  'juntos por el cambio': '#FFCC00',
  'jxc': '#FFCC00',
  'unión por la patria': '#75AADB',
  'union por la patria': '#75AADB',
  'uxp': '#75AADB',
  'primero san nicolás': '#00B4D8',
  'primero san nicolas': '#00B4D8',
};

function colorFor(force) {
  const k = String(force || '').toLowerCase().trim();
  return PALETTE_BY_FORCE[k] || '#6B7280';
}

function isOwnForce(force) {
  const k = String(force || '').toLowerCase();
  return k.includes('libertad avanza') || k.includes('lla') || k.includes('primero san nicolás');
}

function shapeRows(apiRows) {
  return [...(apiRows || [])]
    .sort((a, b) => Number(b.percent || 0) - Number(a.percent || 0))
    .map((r) => ({
      label: r.force,
      pct: Number(r.percent || 0),
      color: colorFor(r.force),
      highlight: isOwnForce(r.force),
    }));
}

const PERIODS = [
  { value: '2025-nac', label: 'Legislativas 2025 (nacionales)', date: '2025-10-26' },
  { value: '2025-pba', label: 'Provinciales 2025 (PBA)', date: '2025-09-07' },
  { value: '2023', label: 'Legislativas 2023', date: '2023-10-22' },
];

const ElectoralSection = () => {
  const [period, setPeriod] = useState('2025-pba');
  const [state, setState] = useState({ loading: true, rows: [], source: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, rows: [], source: null, error: null });
    const def = PERIODS.find((p) => p.value === period);
    listElections(def?.date)
      .then((rows) => {
        if (cancelled) return;
        if (rows && rows.length > 0) {
          setState({ loading: false, rows: shapeRows(rows), source: 'api', error: null });
        } else if (FALLBACK[period]) {
          setState({ loading: false, rows: FALLBACK[period].rows, source: 'fallback', error: null });
        } else {
          setState({ loading: false, rows: [], source: null, error: null });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (FALLBACK[period]) {
          setState({ loading: false, rows: FALLBACK[period].rows, source: 'fallback-error', error: err.message });
        } else {
          setState({ loading: false, rows: [], source: null, error: err.message });
        }
      });
    return () => { cancelled = true; };
  }, [period]);

  const periodLabel = useMemo(
    () => PERIODS.find((p) => p.value === period)?.label || period,
    [period]
  );

  return (
    <ProfileCard>
      <CardTitle>
        <FaChartBar /> Análisis Electoral · San Nicolás
      </CardTitle>
      <CardContent>
        <Header>
          <PeriodSelect value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </PeriodSelect>
        </Header>

        {state.loading && <Loading>Cargando {periodLabel}</Loading>}

        {!state.loading && state.rows.length === 0 && (
          <Empty>
            Sin resultados cargados para {periodLabel}.
            <br />Cargar desde Configuración → Elecciones.
          </Empty>
        )}

        {!state.loading && state.rows.length > 0 && (
          <>
            <ElectionChart>
              {state.rows.map(({ label, pct, color, highlight }) => (
                <BarRow key={label} style={highlight ? { color: 'white' } : undefined}>
                  <BarRowLabel style={highlight ? { color: 'white', fontWeight: 600 } : undefined}>
                    {label}
                  </BarRowLabel>
                  <BarRowTrack>
                    <BarRowFill $pct={pct} color={color} />
                  </BarRowTrack>
                  <BarRowValue>{pct.toFixed(1)}%</BarRowValue>
                </BarRow>
              ))}
            </ElectionChart>
            {(state.source === 'fallback' || state.source === 'fallback-error') && (
              <Hint>
                Fuente: archivo local · pendiente de cargar resultados oficiales
                en Configuración.
              </Hint>
            )}
          </>
        )}
      </CardContent>
    </ProfileCard>
  );
};

export default ElectoralSection;
